import React, { useEffect, useMemo, useState } from "react";
import { fetchFixtureNews, fetchSportsTalkingPoints, fetchTeamNews } from "../api";
import SocialEngagement from "./SocialEngagement";

const toRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.articles)) return payload.articles;
  if (Array.isArray(payload?.news)) return payload.news;
  if (Array.isArray(payload?.talking_points)) return payload.talking_points;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const articleId = (article) =>
  firstDefined(article?.id, article?.article_id, article?.url, article?.link, article?.source_url, article?.title);

const articleUrl = (article) =>
  firstDefined(article?.source_url, article?.url, article?.link, article?.canonical_url, article?.sources?.[0]?.url, "");

const articleImage = (article) =>
  firstDefined(article?.image, article?.image_url, article?.thumbnail, article?.thumbnail_url, article?.url_to_image, "");

const articleBody = (article) =>
  firstDefined(article?.summary, article?.body, article?.description, article?.content, article?.text, "");

const articleSummary = (article) =>
  firstDefined(article?.summary, article?.description, article?.content, article?.text, "");

const articleFullBody = (article) => firstDefined(article?.body, "");

const articleTags = (article) => {
  const raw = firstDefined(article?.tags, []);
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  return String(raw || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
};

const formatTime = (value) => {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function NewsCard({ article, dark, onOpen }) {
  const id = articleId(article);
  const url = articleUrl(article);
  const image = articleImage(article);
  const title = firstDefined(article?.title, article?.headline, "Sports story");
  const source = firstDefined(article?.source_name, article?.source, article?.publisher, article?.sources?.[0]?.name, "Source");
  const published = firstDefined(article?.published_at, article?.published, article?.created_at, article?.date, "");
  const body = articleBody(article);
  const preview = String(body || "");
  const isTruncated = preview.length > 320;
  const cardBg = dark ? "#0a0f0c" : "#ffffff";
  const border = dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
  const text = dark ? "#f8fafc" : "#111827";
  const muted = dark ? "rgba(255,255,255,0.68)" : "rgba(0,0,0,0.62)";

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onOpen(article)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(article);
        }
      }}
      style={{
        border: `1px solid ${border}`,
        background: cardBg,
        borderRadius: 8,
        overflow: "hidden",
        color: text,
        cursor: "pointer",
      }}
    >
      {image ? (
        <img
          src={image}
          alt=""
          style={{ width: "100%", height: 160, objectFit: "cover", display: "block" }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : null}
      <div style={{ padding: 14 }}>
        <div style={{ fontSize: 12, color: muted, marginBottom: 6 }}>
          {source}
          {published ? ` - ${formatTime(published)}` : ""}
        </div>
        <h3 style={{ margin: "0 0 8px", fontSize: 17, lineHeight: 1.3 }}>{title}</h3>
        {body ? (
          <p style={{ margin: 0, color: muted, fontSize: 13, lineHeight: 1.5 }}>
            {preview.slice(0, 320)}
            {isTruncated ? "..." : ""}
          </p>
        ) : null}
        {isTruncated ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(article);
            }}
            style={{
              display: "inline-block",
              marginTop: 10,
              border: "1px solid rgba(52,211,153,0.45)",
              background: "rgba(52,211,153,0.12)",
              color: "#34d399",
              borderRadius: 8,
              padding: "6px 10px",
              fontSize: 13,
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Read more
          </button>
        ) : null}
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: "inline-block",
              marginTop: 10,
              marginLeft: isTruncated ? 8 : 0,
              color: "#34d399",
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            Read at source
          </a>
        ) : null}
        <div onClick={(e) => e.stopPropagation()}>
          <SocialEngagement
            targetType="sports_news_article"
            targetId={id}
            compact
            dark={dark}
            initialLikeCount={article?.like_count ?? article?.likes ?? 0}
            initialLiked={article?.liked_by_me ?? article?.reacted_by_me ?? false}
          />
        </div>
      </div>
    </article>
  );
}

function NewsArticleModal({ article, dark, onClose }) {
  if (!article) return null;

  const id = articleId(article);
  const url = articleUrl(article);
  const image = articleImage(article);
  const title = firstDefined(article?.title, article?.headline, "Sports story");
  const source = firstDefined(article?.source_name, article?.source, article?.publisher, article?.sources?.[0]?.name, "Source");
  const published = firstDefined(article?.published_at, article?.published, article?.created_at, article?.date, "");
  const summary = articleSummary(article);
  const body = articleFullBody(article);
  const tags = articleTags(article);
  const modalBg = dark ? "#0a0f0c" : "#ffffff";
  const text = dark ? "#f8fafc" : "#111827";
  const muted = dark ? "rgba(255,255,255,0.68)" : "rgba(0,0,0,0.62)";
  const border = dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.68)",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "min(48px, 8vh) 16px 24px",
        overflowY: "auto",
      }}
    >
      <article
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(760px, 100%)",
          maxHeight: "calc(100vh - 80px)",
          overflowY: "auto",
          border: `1px solid ${border}`,
          background: modalBg,
          color: text,
          borderRadius: 8,
          boxShadow: "0 24px 70px rgba(0,0,0,0.36)",
        }}
      >
        {image ? (
          <img
            src={image}
            alt=""
            style={{ width: "100%", maxHeight: 280, objectFit: "cover", display: "block" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : null}
        <div style={{ padding: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, color: muted, marginBottom: 7 }}>
                {source}
                {published ? ` - ${formatTime(published)}` : ""}
              </div>
              <h2 style={{ margin: 0, fontSize: 24, lineHeight: 1.25 }}>{title}</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close article"
              style={{
                border: `1px solid ${border}`,
                background: "transparent",
                color: text,
                borderRadius: 8,
                padding: "6px 10px",
                cursor: "pointer",
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              Close
            </button>
          </div>

          {summary ? (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 14 }}>Summary</h3>
              <p style={{ margin: 0, color: muted, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{summary}</p>
            </div>
          ) : null}

          {body ? (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 14 }}>Article</h3>
              <div style={{ color: muted, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{body}</div>
            </div>
          ) : null}

          {tags.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 16 }}>
              {tags.map((tag) => (
                <span
                  key={tag}
                  style={{
                    border: "1px solid rgba(52,211,153,0.32)",
                    background: "rgba(52,211,153,0.12)",
                    color: "#34d399",
                    borderRadius: 8,
                    padding: "3px 7px",
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : null}

          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              style={{ display: "inline-block", marginTop: 16, color: "#34d399", fontSize: 14, fontWeight: 800 }}
            >
              Read at source
            </a>
          ) : null}

          <SocialEngagement
            targetType="sports_news_article"
            targetId={id}
            dark={dark}
            initialLikeCount={article?.like_count ?? article?.likes ?? 0}
            initialLiked={article?.liked_by_me ?? article?.reacted_by_me ?? false}
          />
        </div>
      </article>
    </div>
  );
}

export default function SportsNewsSection({
  mode = "talking-points",
  day,
  fixtureId,
  teamName,
  title,
  limit = 6,
  dark = true,
  emptyMessage = "",
}) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [activeArticle, setActiveArticle] = useState(null);

  const resolvedTitle = useMemo(() => {
    if (title) return title;
    if (mode === "fixture") return "Match News";
    if (mode === "team") return `${teamName || "Team"} News`;
    return "Talking Points";
  }, [mode, teamName, title]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (mode === "fixture" && !fixtureId) return;
      if (mode === "team" && !teamName) return;
      setLoading(true);
      setErr("");
      try {
        const data =
          mode === "fixture"
            ? await fetchFixtureNews(fixtureId, { limit })
            : mode === "team"
              ? await fetchTeamNews(teamName, { limit })
              : await fetchSportsTalkingPoints({ day, limit });
        if (!cancelled) setArticles(toRows(data).filter(Boolean).slice(0, limit));
      } catch {
        if (!cancelled) {
          setArticles([]);
          setErr("News unavailable.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [day, fixtureId, limit, mode, teamName]);

  const muted = dark ? "rgba(255,255,255,0.68)" : "rgba(0,0,0,0.62)";

  if (!loading && !articles.length) {
    if (mode === "talking-points" && emptyMessage) {
      return (
        <section style={{ margin: "18px 0" }}>
          <h2 style={{ margin: "0 0 8px", fontSize: 20, color: dark ? "#f8fafc" : "#111827" }}>{resolvedTitle}</h2>
          <div style={{ fontSize: 13, color: muted }}>{emptyMessage}</div>
        </section>
      );
    }
    return null;
  }

  return (
    <section style={{ margin: "18px 0" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: dark ? "#f8fafc" : "#111827" }}>{resolvedTitle}</h2>
        {loading ? <span style={{ fontSize: 12, color: muted }}>Loading...</span> : null}
      </div>
      {err ? <div style={{ fontSize: 13, color: "#f87171" }}>{err}</div> : null}
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        {articles.map((article, idx) => (
          <NewsCard
            key={articleId(article) || idx}
            article={article}
            dark={dark}
            onOpen={setActiveArticle}
          />
        ))}
      </div>
      <NewsArticleModal article={activeArticle} dark={dark} onClose={() => setActiveArticle(null)} />
    </section>
  );
}
